import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type Theme = 'light' | 'dark' | 'system';

interface ThemeState {
  theme: Theme;
  resolvedTheme: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const getSystemTheme = (): 'light' | 'dark' => {
  if (typeof window === 'undefined') return 'dark';
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
};

const resolveTheme = (theme: Theme): 'light' | 'dark' => {
  if (theme === 'system') {
    return getSystemTheme();
  }
  return theme;
};

const applyTheme = (resolvedTheme: 'light' | 'dark') => {
  if (typeof document === 'undefined') return;

  const root = document.documentElement;
  if (resolvedTheme === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
};

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: 'dark', // Default to dark (matches current design)
      resolvedTheme: 'dark',

      setTheme: (theme: Theme) => {
        const resolvedTheme = resolveTheme(theme);
        applyTheme(resolvedTheme);
        set({ theme, resolvedTheme });
      },

      toggleTheme: () => {
        const { theme } = get();
        // If on system, switch to opposite of current resolved
        // Otherwise just toggle between light and dark
        const newTheme: Theme =
          theme === 'system'
            ? get().resolvedTheme === 'dark'
              ? 'light'
              : 'dark'
            : theme === 'dark'
              ? 'light'
              : 'dark';

        const resolvedTheme = resolveTheme(newTheme);
        applyTheme(resolvedTheme);
        set({ theme: newTheme, resolvedTheme });
      },
    }),
    {
      name: 'after5pm-theme',
      onRehydrateStorage: () => (state) => {
        // Apply theme on initial load
        if (state) {
          const resolvedTheme = resolveTheme(state.theme);
          applyTheme(resolvedTheme);
          // Update resolved theme in case system preference changed
          if (state.resolvedTheme !== resolvedTheme) {
            state.resolvedTheme = resolvedTheme;
          }
        }
      },
    }
  )
);

// Hook for convenient access
export const useTheme = () => {
  const { theme, resolvedTheme, setTheme, toggleTheme } = useThemeStore();
  return {
    theme,
    resolvedTheme,
    setTheme,
    toggleTheme,
    isDark: resolvedTheme === 'dark',
  };
};

// Listen for system theme changes
if (typeof window !== 'undefined') {
  window
    .matchMedia('(prefers-color-scheme: dark)')
    .addEventListener('change', (e) => {
      const state = useThemeStore.getState();
      if (state.theme === 'system') {
        const resolvedTheme = e.matches ? 'dark' : 'light';
        applyTheme(resolvedTheme);
        useThemeStore.setState({ resolvedTheme });
      }
    });

  // Apply theme on initial script load (before React hydrates)
  const stored = localStorage.getItem('after5pm-theme');
  if (stored) {
    try {
      const { state } = JSON.parse(stored);
      if (state?.theme) {
        applyTheme(resolveTheme(state.theme));
      }
    } catch {
      applyTheme('dark');
    }
  } else {
    applyTheme('dark');
  }
}
